import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { DEFAULT_ORG_EXPORT_SETTINGS, type OrgExportSettings } from "@/lib/organizations";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  canAccessIssuedInvoices: boolean;
  canAccessFinancing: boolean;
  canAccessPayments: boolean;
  mustChangePassword: boolean;
  organizationId: string | null;
  orgExportSettings: OrgExportSettings;
  clearMustChangePassword: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  canAccessIssuedInvoices: false,
  canAccessFinancing: false,
  canAccessPayments: false,
  mustChangePassword: false,
  organizationId: null,
  orgExportSettings: DEFAULT_ORG_EXPORT_SETTINGS,
  clearMustChangePassword: () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAccessIssuedInvoices, setCanAccessIssuedInvoices] = useState(false);
  const [canAccessFinancing, setCanAccessFinancing] = useState(false);
  const [canAccessPayments, setCanAccessPayments] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [orgExportSettings, setOrgExportSettings] = useState<OrgExportSettings>(DEFAULT_ORG_EXPORT_SETTINGS);
  const authSyncSeq = useRef(0);

  const loadUserFlags = async (userId: string) => {
    const [{ data: roleRow }, { data: profileRow }] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle(),
      supabase.from("profiles").select("must_change_password, can_access_issued_invoices, can_access_financing, can_access_payments, organization_id").eq("id", userId).maybeSingle(),
    ]);
    const isAdminRole = !!roleRow;
    const orgId = ((profileRow as any)?.organization_id ?? null) as string | null;
    let orgSettings: OrgExportSettings = { ...DEFAULT_ORG_EXPORT_SETTINGS };
    if (orgId) {
      const { data: orgRow } = await supabase
        .from("organizations")
        .select("suenlace_use_invoice_date, suenlace_omit_vat_ledger_number")
        .eq("id", orgId)
        .maybeSingle();
      if (orgRow) {
        orgSettings = {
          useInvoiceDateAsEntryDate: !!(orgRow as any).suenlace_use_invoice_date,
          omitVatLedgerNumber: !!(orgRow as any).suenlace_omit_vat_ledger_number,
        };
      }
    }
    return {
      isAdmin: isAdminRole,
      mustChangePassword: !!(profileRow as any)?.must_change_password,
      // Admin siempre tiene acceso a Facturas emitidas.
      canAccessIssuedInvoices: isAdminRole || !!(profileRow as any)?.can_access_issued_invoices,
      canAccessFinancing: isAdminRole || !!(profileRow as any)?.can_access_financing,
      // Pagos: solo por permiso explícito (inicialmente usuarios de Ferreiro y Portelo).
      canAccessPayments: !!(profileRow as any)?.can_access_payments,
      organizationId: orgId,
      orgExportSettings: orgSettings,
    };
  };

  const syncAuthState = (nextSession: Session | null, blockNavigation = false) => {
    const syncId = ++authSyncSeq.current;
    setSession(nextSession);
    const nextUser = nextSession?.user ?? null;
    setUser(nextUser);

    if (!nextUser) {
      setIsAdmin(false);
      setCanAccessIssuedInvoices(false);
      setCanAccessFinancing(false); setCanAccessPayments(false);
      setMustChangePassword(false);
      setOrganizationId(null);
      setOrgExportSettings(DEFAULT_ORG_EXPORT_SETTINGS);
      setLoading(false);
      return;
    }

    if (blockNavigation) setLoading(true);
    loadUserFlags(nextUser.id)
      .then((flags) => {
        if (syncId !== authSyncSeq.current) return;
        setIsAdmin(flags.isAdmin);
        setMustChangePassword(flags.mustChangePassword);
        setCanAccessIssuedInvoices(flags.canAccessIssuedInvoices);
        setCanAccessFinancing(flags.canAccessFinancing);
        setCanAccessPayments(flags.canAccessPayments);
        setOrganizationId(flags.organizationId);
        setOrgExportSettings(flags.orgExportSettings);
      })
      .catch(() => {
        if (syncId !== authSyncSeq.current) return;
        setIsAdmin(false);
        setCanAccessIssuedInvoices(false);
      setCanAccessFinancing(false); setCanAccessPayments(false);
        setMustChangePassword(false);
        setOrganizationId(null);
        setOrgExportSettings(DEFAULT_ORG_EXPORT_SETTINGS);
      })
      .finally(() => {
        if (syncId === authSyncSeq.current) setLoading(false);
      });
  };

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Eventos que NO deben provocar remount de la app (mantenemos
      // los archivos en proceso y el estado en memoria intactos):
      //   - TOKEN_REFRESHED: refresco silencioso del JWT cada ~1h
      //   - USER_UPDATED:    cambios menores del usuario
      // Para estos sólo actualizamos la session/usuario sin tocar
      // `loading` ni recargar flags (que ya tenemos en memoria).
      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        return;
      }

      // SIGNED_IN / SIGNED_OUT / INITIAL_SESSION / PASSWORD_RECOVERY → sync completo.
      setTimeout(() => syncAuthState(nextSession), 0);
    });

    supabase.auth.getSession().then(({ data: { session: restoredSession } }) => {
      syncAuthState(restoredSession, true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const clearMustChangePassword = () => {
    authSyncSeq.current += 1;
    setMustChangePassword(false);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, canAccessIssuedInvoices, canAccessFinancing, canAccessPayments, mustChangePassword, organizationId, orgExportSettings, clearMustChangePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
